import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router-dom';
import { dashboardApi } from '../../api/endpoints.js';
import { useApi } from '../../hooks/useApi.js';
import { useAuth } from '../../auth/AuthContext.jsx';
import PageHeader from '../../components/PageHeader.jsx';
import StatCard from '../../components/StatCard.jsx';
import Card from '../../components/Card.jsx';
import Badge from '../../components/Badge.jsx';
import Spinner from '../../components/Spinner.jsx';
import EmptyState from '../../components/EmptyState.jsx';
import { formatDate, fullName } from '../../utils/format.js';
import { PROJECT_STATUSES } from '../../constants.js';

/**
 * THE IT DASHBOARD - the work this team is doing, and nothing else.
 *
 * The generic DashboardPage is a sales dashboard: pipeline value, hot leads,
 * meetings this week. None of that is IT's work, so this screen is separate
 * rather than another branch in there, and it answers the three questions the
 * team actually opens it for:
 *
 *   how much is there   -> the tiles: accounts, projects to do / in progress /
 *                          delivered, tasks still open
 *   what is late        -> overdue projects and tasks, called out in red
 *   who is on what      -> one card per IT person, listing their open projects
 *
 * Every number here is already scoped by the API to signed customers, so a
 * prospect sales is still cold-calling never reaches this page.
 */

/**
 * The tiles: the accounts, then the work, then the arrears.
 *
 * A summary, not a breakdown - the six columns of the board already give the
 * per-status detail, and repeating it here as six more tiles would only push
 * the useful line (what is late) off the first screen. Every tile links into
 * the list it counts.
 */
const KPIS = [
  { key: 'customers', to: '/it/customers' },
  { key: 'projectsOpen', to: '/it/projects?view=board' },
  { key: 'projectsToDo', to: '/it/projects?status=TO_DO' },
  { key: 'projectsDelivered', to: '/it/projects?status=DELIVERED' },
  { key: 'tasksOpen', to: '/tasks' },
  { key: 'projectsOverdue', to: '/it/projects?open=yes', tone: 'danger' },
];

const ItDashboardPage = () => {
  const { t, i18n } = useTranslation();
  const { user } = useAuth();
  const navigate = useNavigate();
  const locale = i18n.resolvedLanguage;

  const { data, loading, error } = useApi(() => dashboardApi.get(), []);

  if (loading) return <Spinner full />;
  if (error) return <EmptyState title={t('common.error')} description={error.message} />;

  const kpis = data?.kpis ?? {};
  const workload = data?.workload ?? [];
  const byStatus = data?.charts?.projectsByStatus ?? {};
  const totalProjects = Object.values(byStatus).reduce((a, b) => a + b, 0);

  const projectLink = (project) => (
    <li key={project.id} className="timeline__item">
      <div className="timeline__main">
        <button
          type="button"
          className="task__title"
          onClick={() => navigate(`/it/projects?project=${project.id}`)}
        >
          {project.code ? `${project.code} - ` : ''}
          {project.title}
        </button>
        <p className="timeline__meta">
          {project.customerName ?? '-'}
          {project.dueAt ? ` - ${t('projects.dueAt')} ${formatDate(project.dueAt, locale)}` : ''}
        </p>
      </div>
      {project.overdue ? (
        <Badge value="LOST" label={t('projects.overdue')} />
      ) : (
        <Badge value={project.status} label={t(`projects.status.${project.status}`)} />
      )}
    </li>
  );

  return (
    <>
      <PageHeader
        title={t('dashboard.greeting', { name: user.firstName })}
        subtitle={t('it.dashboardSubtitle')}
        actions={
          <Link className="btn btn--primary" to="/it/projects?view=board">
            {t('projects.viewBoard')}
          </Link>
        }
      />

      <div className="stat-grid">
        {KPIS.map(({ key, to, tone }) => (
          <Link key={key} to={to} className="stat-link">
            <StatCard
              label={t(`dashboard.${key}`)}
              value={kpis[key] ?? 0}
              tone={tone && kpis[key] ? tone : 'default'}
            />
          </Link>
        ))}
      </div>

      {/* Yours, separated from the team's: on a shared board the first thing
          anybody looks for is their own line. */}
      <Card title={t('dashboard.myWork')}>
        <div className="header-actions">
          <Badge value="DEVELOPING" label={`${kpis.myProjectsOpen ?? 0} ${t('dashboard.myProjectsOpen')}`} />
          <Badge value="TO_DO" label={`${kpis.myTasksOpen ?? 0} ${t('dashboard.myTasksOpen')}`} />
          {kpis.tasksOverdue ? (
            <Badge value="LOST" label={`${kpis.tasksOverdue} ${t('dashboard.tasksOverdue')}`} />
          ) : null}
          {kpis.projectsUnassigned ? (
            <Badge value="WARM" label={`${kpis.projectsUnassigned} ${t('dashboard.projectsUnassigned')}`} />
          ) : null}
        </div>
      </Card>

      {/* Who is on what. One card per person, including the people with nothing
          on their plate - a missing row reads as "no data", a zero reads as
          "free", and those are opposite answers for whoever is allocating. */}
      <h2 className="section-title">{t('dashboard.teamWorkload')}</h2>
      {workload.length ? (
        <div className="grid grid--2">
          {workload.map((row) => (
            <Card
              key={row.user?.id ?? 'unassigned'}
              title={row.user ? fullName(row.user) : t('projects.unassigned')}
              actions={
                <div className="header-actions">
                  <Badge value="TO_DO" label={`${row.counts.TO_DO} ${t('projects.status.TO_DO')}`} />
                  {/* One badge for everything under way rather than three - the
                      board is where you look at which column each one is in. */}
                  <Badge value="DEVELOPING" label={`${row.counts.active} ${t('projects.active')}`} />
                  {row.counts.overdue ? (
                    <Badge value="LOST" label={`${row.counts.overdue} ${t('projects.overdue')}`} />
                  ) : null}
                </div>
              }
            >
              {row.projects.length ? (
                <ul className="timeline">{row.projects.map(projectLink)}</ul>
              ) : (
                <EmptyState title={t('projects.noOpenProject')} />
              )}
              {row.user ? (
                <p className="muted field__hint">
                  {t('dashboard.openTasksCount', { count: row.counts.openTasks })}
                </p>
              ) : null}
            </Card>
          ))}
        </div>
      ) : (
        <Card>
          <EmptyState title={t('projects.empty')} description={t('projects.emptyHint')} />
        </Card>
      )}

      <div className="grid grid--2">
        <Card title={t('dashboard.projectsByStatus')}>
          {totalProjects ? (
            <ul className="bar-list">
              {PROJECT_STATUSES.map((status) => (
                <li key={status} className="bar-list__row">
                  <span className="bar-list__label">{t(`projects.status.${status}`)}</span>
                  <span className="bar-list__track">
                    <span
                      className="bar-list__fill"
                      style={{ width: `${((byStatus[status] ?? 0) / totalProjects) * 100}%` }}
                    />
                  </span>
                  <span className="bar-list__value">{byStatus[status] ?? 0}</span>
                </li>
              ))}
            </ul>
          ) : (
            <EmptyState title={t('common.noData')} />
          )}
        </Card>

        <Card title={t('dashboard.dueSoon')}>
          {data?.dueSoon?.length ? (
            <ul className="timeline">{data.dueSoon.map(projectLink)}</ul>
          ) : (
            <EmptyState title={t('projects.nothingDueSoon')} />
          )}
        </Card>
      </div>
    </>
  );
};

export default ItDashboardPage;
